import { useMemo } from 'react';
import { BrandingProductType } from 'vopay';
import type { BrandingHostProps } from 'shared/branding';
import { usePermissions } from '@/providers/Permissions.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useBranding } from '@/providers/Branding.provider';

// Mirrors client-dashboard's useBrandingHostProps, plus the Application Form (Onboarding) product and the
// view-only role. SettingsPage's PartnerSettingsApiProvider points the page at the partner branding routes.
export default function useBrandingHostProps(): BrandingHostProps {
  const { permissions } = usePermissions();
  const { setBrandingValues, defaultPrimaryColor, defaultSecondaryColor } = useBranding();

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const { BrandingPortalEnabled, BrandingELinxEnabled, BrandingEmailEnabled, BrandingOnboardingEnabled } = permissions;

  const products = useMemo(
    () =>
      [
        { productType: BrandingProductType.Portal, enabled: BrandingPortalEnabled },
        { productType: BrandingProductType.Elinx, enabled: BrandingELinxEnabled },
        { productType: BrandingProductType.Email, enabled: BrandingEmailEnabled },
        { productType: BrandingProductType.Onboarding, enabled: BrandingOnboardingEnabled },
      ]
        .filter((option) => option.enabled)
        .map((option) => option.productType),
    [BrandingPortalEnabled, BrandingELinxEnabled, BrandingEmailEnabled, BrandingOnboardingEnabled],
  );

  const defaultColors = useMemo(
    () => ({ primary: defaultPrimaryColor, secondary: defaultSecondaryColor }),
    [defaultPrimaryColor, defaultSecondaryColor],
  );

  return {
    products,
    defaultColors,
    readOnly: isRoleViewOnly,
    onPortalBrandingSaved: (branding) =>
      setBrandingValues({
        primaryColor: branding.BrandingDetails?.PrimaryColor || defaultPrimaryColor,
        secondaryColor: branding.BrandingDetails?.SecondaryColor || defaultSecondaryColor,
        logo: branding.Logo,
        smallLogo: branding.SmallLogo,
        appTitle: branding.AppTitle,
        appFavicon: branding.Favicon,
      }),
  };
}
